import { Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from 'app/core/auth/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    /**
     * Single source of truth for "refresh in progress".
     * - null   → no refresh active, the next 401 should kick off a refresh
     * - string → in-flight; queued requests wait for it to flip non-null with the new token
     */
    private _refreshGate$ = new BehaviorSubject<string | null>(null);
    private _refreshing = false;

    constructor(private _authService: AuthService, private _router: Router) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const authReq = this._attach(req, this._authService.accessToken);

        return next.handle(authReq).pipe(
            catchError((error) => {
                if (!(error instanceof HttpErrorResponse)) {
                    return throwError(error);
                }

                // Friendly route-level handling for non-auth errors.
                if (error.status === 404) {
                    this._router.navigate(['error-404']);
                    return throwError(error);
                }
                if (error.status === 500) {
                    this._router.navigate(['error-500']);
                    return throwError(error);
                }

                if (error.status !== 401) {
                    return throwError(error);
                }

                // 401 on the refresh endpoint itself = unrecoverable.
                if (this._isAuthEndpoint(req.url)) {
                    this._forceSignIn('expired');
                    return throwError(error);
                }

                return this._handle401(req, next);
            }),
        );
    }

    // -----------------------------------------------------------------------
    // 401 handling — coalesce concurrent failures into one refresh attempt
    // -----------------------------------------------------------------------

    private _handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this._refreshing) {
            // Wait for the existing refresh to finish, then replay this request with the new token.
            return this._refreshGate$.pipe(
                filter((token): token is string => token !== null),
                take(1),
                switchMap((token) => next.handle(this._attach(req, token))),
            );
        }

        this._refreshing = true;
        this._refreshGate$.next(null);

        return this._authService.refreshAccessToken().pipe(
            switchMap((ok) => {
                this._refreshing = false;
                if (!ok) {
                    // Only hard sign-out when the session is genuinely gone. A transient
                    // refresh failure (network/CORS/5xx) on a still-valid token shouldn't
                    // kick an actively-working user — let this request error and recover
                    // on the next call instead.
                    if (this._authService.isAccessTokenExpired()) {
                        this._forceSignIn('expired');
                    }
                    return throwError(new HttpErrorResponse({ status: 401 }));
                }
                const newToken = this._authService.accessToken;
                // Wake up any queued waiters.
                this._refreshGate$.next(newToken);
                return next.handle(this._attach(req, newToken));
            }),
            catchError((err) => {
                this._refreshing = false;
                this._forceSignIn('expired');
                return throwError(err);
            }),
        );
    }

    private _attach(req: HttpRequest<any>, token: string): HttpRequest<any> {
        const isAuthEndpoint = this._isAuthEndpoint(req.url);

        // Auth endpoints need the cookie for the httpOnly refresh-token flow.
        const withCredentials = isAuthEndpoint || req.withCredentials;

        if (token) {
            return req.clone({
                setHeaders: { Authorization: `Bearer ${token}` },
                withCredentials,
            });
        }
        // No token: still pass withCredentials through for auth endpoints (e.g. refresh).
        return withCredentials !== req.withCredentials
            ? req.clone({ withCredentials })
            : req;
    }

    private _isAuthEndpoint(url: string): boolean {
        return /\/Auth\/(login|refresh|logout|logout-others|otp\/validate|otp\/resend)\b/i.test(url);
    }

    private _forceSignIn(reason: 'expired' | 'kicked'): void {
        // Hard reset so anything cached in services drops with the token.
        try {
            this._authService.signOut().subscribe();
        } catch {
            /* ignore */
        }
        // Navigate via the router so we don't full-reload the SPA when the user is
        // already on the sign-in page (e.g. visiting a stale link).
        this._router.navigate(['/sign-in'], { queryParams: { reason } });
    }
}
